import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { DatabaseService } from './database.service';
import { tap, map, catchError } from 'rxjs/operators';

export interface User {
  id: string;
  username: string;
  email: string;
  name: string;
  role: 'admin' | 'mesero' | 'cocinero' | 'cajero';
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private database: DatabaseService) {
    // Esperar un poco para que IndexedDB esté listo
    setTimeout(() => {
      this.loadUser();
    }, 300);
  }

  private loadUser(): void {
    this.database.getUser().subscribe({
      next: (user) => {
        if (user) {
          this.currentUserSubject.next(user);
        }
      },
      error: (error) => {
        console.error('Error loading user:', error);
      }
    });
  }

  login(username: string, password: string): Observable<boolean> {
    return new Observable(observer => {
      // Simulación de login - en producción esto sería una llamada HTTP
      setTimeout(() => {
        // Usuarios de prueba
        const users: { [key: string]: { password: string; user: User } } = {
          'admin': {
            password: 'admin123',
            user: {
              id: '1',
              username: 'admin',
              email: 'admin@restora360.com',
              name: 'Administrador',
              role: 'admin'
            }
          },
          'mesero': {
            password: 'mesero123',
            user: {
              id: '2',
              username: 'mesero',
              email: 'mesero@restora360.com',
              name: 'Juan Mesero',
              role: 'mesero'
            }
          },
          'cocinero': {
            password: 'cocinero123',
            user: {
              id: '3',
              username: 'cocinero',
              email: 'cocinero@restora360.com',
              name: 'Carlos Cocinero',
              role: 'cocinero'
            }
          },
          'cajero': {
            password: 'cajero123',
            user: {
              id: '4',
              username: 'cajero',
              email: 'cajero@restora360.com',
              name: 'María Cajero',
              role: 'cajero'
            }
          }
        };

        const userData = users[username.toLowerCase()];
        
        if (userData && userData.password === password) {
          // Guardar usuario en IndexedDB
          this.database.setUser(userData.user).subscribe({
            next: () => {
              this.currentUserSubject.next(userData.user);
              observer.next(true);
              observer.complete();
            },
            error: (error) => {
              console.error('Error saving user:', error);
              observer.next(false);
              observer.complete();
            }
          });
        } else {
          observer.next(false);
          observer.complete();
        }
      }, 500); // Simular delay de red
    });
  }

  logout(): void {
    // Limpiar usuario en memoria primero para que al navegar a /login
    // el LoginComponent no vea isAuthenticated() y redirija de vuelta al dashboard
    this.currentUserSubject.next(null);
    this.database.clearUser().subscribe({
      error: (error) => {
        console.error('Error clearing user:', error);
      }
    });
  }

  isAuthenticated(): boolean {
    return this.currentUserSubject.value !== null;
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }
}
